import * as tus from "tus-js-client";
import { api } from "@/lib/api";

const CHUNK = 6 * 1024 * 1024;
export const MAX_IMAGE_MB = 25;
export const MAX_VIDEO_MB = 50;
export const MAX_STREAM_VIDEO_MB = 2048;
const CF_CHUNK = 10 * 1024 * 1024;

// Cloudflare Stream: backend issues a one-time TUS URL; bytes go straight to Cloudflare.
function cfTusUpload(file, uploadUrl, onProgress) {
  return new Promise((resolve, reject) => {
    const up = new tus.Upload(file, {
      uploadUrl,
      chunkSize: CF_CHUNK,
      retryDelays: [0, 2000, 5000, 10000, 20000],
      storeFingerprintForResuming: false,
      onProgress: (sent, total) => onProgress?.(Math.round((sent / total) * 100)),
      onError: reject,
      onSuccess: resolve,
    });
    up.start();
  });
}

async function streamUpload(file, onProgress) {
  let init;
  try {
    ({ data: init } = await api.post("/creator/assets/stream-upload", { filename: file.name, size: file.size }));
  } catch (e) {
    if (e.response?.status === 503) return null;
    throw e;
  }
  await cfTusUpload(file, init.upload_url, onProgress);
  onProgress?.(100);
  const { data } = await api.post(`/creator/assets/${init.asset_id}/stream-complete`);
  return data;
}

async function legacyUpload(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/creator/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

function tusUpload(file, init, onProgress) {
  return new Promise((resolve, reject) => {
    const up = new tus.Upload(file, {
      endpoint: init.tus_endpoint,
      retryDelays: [0, 2000, 5000, 10000],
      headers: { "x-signature": init.token, "x-upsert": "false" },
      uploadDataDuringCreation: true,
      // Each upload targets a unique object; never resume another attempt's upload (iOS reuses file names).
      storeFingerprintForResuming: false,
      chunkSize: CHUNK,
      metadata: { bucketName: init.bucket, objectName: init.object_name, contentType: init.content_type, cacheControl: "3600" },
      onProgress: (sent, total) => onProgress?.(Math.round((sent / total) * 100)),
      onError: reject,
      onSuccess: resolve,
    });
    up.start();
  });
}

// Downscale large photos (e.g. iPhone PNG/HEIC exports) before upload; keeps EXIF orientation via createImageBitmap.
export async function optimizeImage(file, maxSide = 2000, quality = 0.88) {
  if (!(file.type || "").startsWith("image/") || file.type === "image/gif" || !window.createImageBitmap) return file;
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], (file.name || "image").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}

function putUpload(file, init, onProgress) {
  // XHR (not fetch) so iPhone users see real progress on single-request uploads.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", init.upload_url);
    xhr.setRequestHeader("Content-Type", init.content_type);
    xhr.setRequestHeader("x-upsert", "false");
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(Math.round((e.loaded / e.total) * 99));
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(file);
  });
}

// Direct-to-Supabase upload via a server-issued one-time token; falls back to the
// existing storage when Supabase isn't configured yet.
export async function uploadCreatorFile(file, onProgress) {
  const isVideo = (file.type || "").startsWith("video/") || /\.(mp4|webm|mov|m4v)$/i.test(file.name);
  if (isVideo) {
    if (file.size > MAX_STREAM_VIDEO_MB * 1024 * 1024) throw new Error(`${file.name} is too large. Max 2 GB for videos.`);
    const streamed = await streamUpload(file, onProgress);
    if (streamed) return streamed;
  }
  const maxMb = isVideo ? MAX_VIDEO_MB : MAX_IMAGE_MB;
  if (file.size > maxMb * 1024 * 1024) throw new Error(`${file.name} is too large. Max ${maxMb}MB for ${isVideo ? "videos" : "photos"}.`);
  let init;
  try {
    ({ data: init } = await api.post("/creator/assets/direct-upload", { filename: file.name, size: file.size }));
  } catch (e) {
    if (e.response?.status === 503) return legacyUpload(file);
    throw e;
  }
  if (init.resumable) {
    try {
      await tusUpload(file, init, onProgress);
    } catch {
      await putUpload(file, init, onProgress);
    }
  } else {
    await putUpload(file, init, onProgress);
  }
  onProgress?.(100);
  const { data } = await api.post(`/creator/assets/${init.asset_id}/complete`);
  return data;
}
